import { toast } from 'vue-sonner';
import { createTrackListExport } from '@/composables/api/useTrackListExportApi';
import { useTrackListExportStore } from '@/stores/trackListExportStore';
import type { TrackListExportSource } from '@/types/trackListExport';

/**
 * Start a spreadsheet export of one list, and tell the person how it went.
 *
 * The export column list calls this from its Export button. It is a plain
 * function, not part of the button that opened the list. The inspector swaps
 * its views, so that button may be gone by the time the person presses Export.
 *
 * Returns true when the export started. On false, a toast has said why.
 */
export async function startListExport(request: {
  source: TrackListExportSource;
  columns: string[];
  /** The list's name for the toast and the email, such as "Account search". */
  label: string;
}): Promise<boolean> {
  try {
    const response = await createTrackListExport({
      source: request.source,
      columns: request.columns,
    });

    useTrackListExportStore().track({
      operationId: response.operation_id,
      scope: response.async_operation_scope,
      label: request.label,
      startedAt: Date.now(),
    });

    // The endpoint names what it dropped. Reading the list instead of
    // subtracting lengths fixes a miscount: a column sent as `artist` comes
    // back accepted as `artist_name`, and the arithmetic called that a drop and
    // warned about a column the user actually got.
    const unexpectedlyDropped = response.dropped_columns ?? [];

    // Singular and plural both read as English. The verb has to agree too, so
    // it is not enough to add an "s" to the noun.
    const droppedSentence = unexpectedlyDropped.length === 1
      ? "1 column isn't available for this list and was left out."
      : `${unexpectedlyDropped.length} columns aren't available for this list and were left out.`;

    toast.success('Building your spreadsheet…', {
      description: unexpectedlyDropped.length > 0
        // "records", not "tracks": the same list exports an Account search.
        ? `${response.row_count} records. ${droppedSentence}`
        : `${response.row_count} records. We'll tell you here and by email when it's ready.`,
    });
    return true;
  } catch (error: unknown) {
    const response = (error as { response?: { status?: number; data?: { message?: string } } }).response;

    // The cap refusal and the permission refusal both carry a message worth
    // showing verbatim. They tell the user what to do next.
    if (response?.status === 422 || response?.status === 403) {
      toast.error(response.data?.message ?? 'That export was refused.');
    } else {
      toast.error("Couldn't start the export. Try again.");
    }
    return false;
  }
}
